import { api } from './api';

/**
 * First-party usage telemetry — batched, buffered, and invisible.
 *
 * `track(event, props)` queues events into a localStorage-backed buffer and
 * flushes them to `POST /telemetry/events` (batch endpoint, server stamps
 * identity from the auth token). Flush triggers: every 15s, when 20+ events
 * are queued, on tab-hide, and when the browser comes back online.
 *
 * Hard rules:
 * - Telemetry must NEVER break the app: every public function swallows all
 *   errors, and a failed flush either retries later (network/5xx) or drops
 *   the batch silently (any 4xx — unauthenticated or poison payload).
 * - Offline-safe: events buffer (max 200, oldest dropped) and flush when
 *   connectivity returns; the server clamps stale timestamps.
 * - No PII in props. Identity is never sent — the server derives it.
 *
 * sendBeacon is deliberately NOT used on tab-hide: it cannot carry the
 * Authorization header the ingest endpoint requires. Instead we fire an
 * ordinary api.post and let the localStorage buffer cover the case where
 * the tab dies before it lands (at-least-once delivery).
 */

export type TelemetryProps = Record<string, unknown>;

type QueuedEvent = {
  event: string;
  occurred_at: string;
  surface: 'lite' | 'hub';
  session_id: string;
  site_id?: string;
  inspection_id?: string;
  duration_ms?: number;
  props: Record<string, unknown>;
};

type DataRouterLike = {
  state: { location: { pathname: string } };
  subscribe: (
    listener: (state: { location: { pathname: string } }) => void
  ) => () => void;
};

const QUEUE_KEY = 'envirolink.telemetry.queue.v1';
const SESSION_KEY = 'envirolink.telemetry.session';
const EVENT_NAME_RE = /^[a-z0-9_.]{1,60}$/;
const MAX_QUEUE = 200;
const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_THRESHOLD = 20;
const BATCH_MAX = 50;
// Server rejects props > 2048 bytes serialized; keep client margin under it.
const MAX_PROPS_CHARS = 1900;
const ERROR_DEDUPE_MS = 5 * 60 * 1000;

// Paths that are not the hub surface (lite, auth, and public token pages).
const NON_HUB_PREFIXES = [
  '/lite',
  '/login',
  '/signup',
  '/sign/',
  '/public',
  '/verify',
  '/oidc',
  '/accept-invite',
  '/pending-approval',
  '/select-mode',
];

let queue: QueuedEvent[] = loadQueue();
let flushing = false;
let listenersInstalled = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let fallbackSessionId: string | null = null;

function safeStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function loadQueue(): QueuedEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE) : [];
  } catch {
    return [];
  }
}

function persistQueue(): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Storage full/unavailable — the in-memory queue still works this tab.
  }
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Random per-tab session id (sessionStorage-backed, in-memory fallback). */
export function getTelemetrySessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = randomId().slice(0, 40);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    if (!fallbackSessionId) fallbackSessionId = randomId().slice(0, 40);
    return fallbackSessionId;
  }
}

function currentSurface(): 'lite' | 'hub' {
  try {
    return window.location.pathname.startsWith('/lite') ? 'lite' : 'hub';
  } catch {
    return 'hub';
  }
}

function ensureListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  flushTimer = setInterval(() => void flushTelemetry(), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    // Fire-and-forget on hide; the localStorage buffer covers a dying tab.
    if (document.visibilityState === 'hidden') void flushTelemetry();
  });
  window.addEventListener('online', () => void flushTelemetry());
}

/**
 * Queue one usage event. Reserved prop keys `site_id`, `inspection_id`,
 * `duration_ms`, and `surface` are lifted into their own columns; everything
 * else travels in the props JSON (≤ ~2KB serialized, no PII).
 */
export function track(event: string, props: TelemetryProps = {}): void {
  try {
    if (!EVENT_NAME_RE.test(event)) {
      if (import.meta.env.DEV) {
        console.warn(`[telemetry] dropped invalid event name: ${event}`);
      }
      return;
    }
    const { site_id, inspection_id, duration_ms, surface, ...rest } =
      props as Record<string, unknown>;
    let cleanProps: Record<string, unknown> = rest;
    const serialized = safeStringify(cleanProps);
    if (serialized === null || serialized.length > MAX_PROPS_CHARS) {
      cleanProps = { _truncated: true };
    }
    const item: QueuedEvent = {
      event,
      occurred_at: new Date().toISOString(),
      surface:
        surface === 'lite' || surface === 'hub' ? surface : currentSurface(),
      session_id: getTelemetrySessionId(),
      props: cleanProps,
    };
    if (typeof site_id === 'string' && site_id) item.site_id = site_id;
    if (typeof inspection_id === 'string' && inspection_id) {
      item.inspection_id = inspection_id;
    }
    if (typeof duration_ms === 'number' && Number.isFinite(duration_ms)) {
      item.duration_ms = Math.max(0, Math.round(duration_ms));
    }
    queue.push(item);
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
    persistQueue();
    ensureListeners();
    if (queue.length >= FLUSH_THRESHOLD) void flushTelemetry();
  } catch {
    // Telemetry never throws into the app.
  }
}

/**
 * Drain the buffer in ≤50-event batches. Network/5xx failures keep the
 * batch buffered for a later flush; any 4xx drops it silently (expired
 * session or a poison event must not wedge the queue). Never throws.
 */
export async function flushTelemetry(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    while (queue.length > 0) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        break;
      }
      const batch = queue.slice(0, BATCH_MAX);
      try {
        await api.post('/telemetry/events', batch);
        queue.splice(0, batch.length);
        persistQueue();
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          queue.splice(0, batch.length);
          persistQueue();
        }
        break; // retry later on network/5xx; don't hammer after a 4xx either
      }
    }
  } catch {
    // Never throws.
  } finally {
    flushing = false;
  }
}

// ─── Global JS error capture ────────────────────────────────────────────────

const recentErrors = new Map<string, number>();

function isNoise(message: string, source: string, stack: string): boolean {
  if (!message || message === 'Script error.' || message === 'Script error') {
    return true;
  }
  return /(chrome|moz|safari)-extension:\/\//.test(`${source} ${stack}`);
}

function dedupeKey(message: string, stack: string): string {
  return `${message}||${stack.slice(0, 200)}`;
}

function shouldReportError(message: string, stack: string): boolean {
  const now = Date.now();
  const key = dedupeKey(message, stack);
  const last = recentErrors.get(key);
  if (last !== undefined && now - last < ERROR_DEDUPE_MS) return false;
  if (recentErrors.size > 100) {
    for (const [k, t] of recentErrors) {
      if (now - t >= ERROR_DEDUPE_MS) recentErrors.delete(k);
    }
  }
  recentErrors.set(key, now);
  return true;
}

function trackClientError(
  kind: 'error' | 'unhandledrejection',
  message: string,
  stack: string,
  source: string
): void {
  if (isNoise(message, source, stack)) return;
  if (!shouldReportError(message, stack)) return;
  const base = {
    kind,
    message: message.slice(0, 300),
    url: String(window.location.href).slice(0, 300),
    user_agent: String(navigator.userAgent).slice(0, 200),
  };
  // Fit the stack into whatever room is left under the server's props cap.
  const room = MAX_PROPS_CHARS - (safeStringify(base)?.length ?? 0) - 100;
  track('client_error', { ...base, stack: stack.slice(0, Math.max(0, room)) });
}

/**
 * window.onerror + unhandledrejection → track('client_error'). Dedupes the
 * same message+stack to once per 5 minutes per session and drops extension /
 * cross-origin noise. Install once from main.tsx.
 */
export function installGlobalErrorCapture(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e: ErrorEvent) => {
    try {
      trackClientError(
        'error',
        String(e.message ?? ''),
        String(e.error?.stack ?? ''),
        String(e.filename ?? '')
      );
    } catch {
      // never throw from an error handler
    }
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    try {
      const reason = e.reason as { message?: unknown; stack?: unknown } | null;
      const message =
        reason && typeof reason.message === 'string'
          ? reason.message
          : String(e.reason ?? '');
      const stack =
        reason && typeof reason.stack === 'string' ? reason.stack : '';
      trackClientError('unhandledrejection', message, stack, '');
    } catch {
      // never throw from an error handler
    }
  });
}

// ─── Hub page views ─────────────────────────────────────────────────────────

let lastHubPath: string | null = null;

function isHubPath(path: string): boolean {
  return !NON_HUB_PREFIXES.some((p) => path === p || path.startsWith(p));
}

/**
 * Router-level page-view tracking for the hub surface: one
 * `hub_page_view {path}` per route change (lite/auth/public paths excluded —
 * the Lite surface has its own dedicated events).
 */
export function installHubPageViewTracking(router: DataRouterLike): void {
  try {
    const report = (path: string) => {
      if (path === lastHubPath) return;
      lastHubPath = path;
      if (isHubPath(path)) track('hub_page_view', { path, surface: 'hub' });
    };
    report(router.state.location.pathname);
    router.subscribe((state) => report(state.location.pathname));
  } catch {
    // Telemetry never breaks the app.
  }
}

// ─── Test hooks ─────────────────────────────────────────────────────────────

/** Internal — vitest only. */
export function __resetTelemetryForTests(): void {
  queue = [];
  flushing = false;
  lastHubPath = null;
  recentErrors.clear();
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  listenersInstalled = false;
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch {
    // ignore
  }
}

/** Internal — vitest only. */
export function __getTelemetryQueueForTests(): readonly QueuedEvent[] {
  return queue;
}
